import React from 'react';
import Icon from '../../../components/AppIcon';

const TimingRecommendations = ({ data, loading = false }) => {
  if (loading || !data) {
    return (
      <div className="space-y-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-muted rounded w-1/4 mb-4" />
            <div className="space-y-3">
              <div className="h-4 bg-muted rounded" />
              <div className="h-4 bg-muted rounded w-3/4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const promotion = data?.timingRecommendation?.promotion;
  const salaryIncrease = data?.timingRecommendation?.salaryIncrease;
  const preparationPlan = data?.preparationPlan;

  const getTimingIcon = (recommended) => {
    return recommended ? 'CheckCircle' : 'Clock';
  };

  const getTimingColor = (recommended) => {
    return recommended ? 'text-success' : 'text-warning';
  };

  return (
    <div className="space-y-6">
      {/* Promotion Timing */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            promotion?.recommended ? 'bg-success/10' : 'bg-warning/10'
          }`}>
            <Icon 
              name={getTimingIcon(promotion?.recommended)} 
              size={16} 
              className={getTimingColor(promotion?.recommended)} 
            />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Promotion Timing</h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-2">Current Assessment</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Recommended</span>
                  <span className={`text-sm font-medium ${getTimingColor(promotion?.recommended)}`}>
                    {promotion?.recommended ? 'Yes' : 'Not Yet'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Timeframe</span>
                  <span className="text-sm font-medium text-foreground">
                    {promotion?.timeframe || 'TBD'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Confidence</span>
                  <span className="text-sm font-medium text-foreground">
                    {Math.round((promotion?.confidence || 0) * 100)}%
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-2">Reasoning</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {promotion?.reasoning || 'Analysis in progress...'}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-3">Immediate Actions</h4>
              <div className="space-y-2">
                {preparationPlan?.immediateActions?.slice(0, 3)?.map((action, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">{action}</p>
                  </div>
                )) || (
                  <p className="text-sm text-muted-foreground italic">
                    Generating action plan...
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Salary Increase Timing */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            salaryIncrease?.recommended ? 'bg-success/10' : 'bg-warning/10'
          }`}>
            <Icon 
              name={getTimingIcon(salaryIncrease?.recommended)} 
              size={16} 
              className={getTimingColor(salaryIncrease?.recommended)} 
            />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Salary Increase Timing</h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-2">Current Assessment</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Recommended</span>
                  <span className={`text-sm font-medium ${getTimingColor(salaryIncrease?.recommended)}`}>
                    {salaryIncrease?.recommended ? 'Yes' : 'Not Yet'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Timeframe</span>
                  <span className="text-sm font-medium text-foreground">
                    {salaryIncrease?.timeframe || 'TBD'}
                  </span>
                </div>
                {salaryIncrease?.suggestedIncrease && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Suggested</span>
                    <span className="text-sm font-medium text-foreground">
                      {salaryIncrease?.suggestedIncrease}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Confidence</span>
                  <span className="text-sm font-medium text-foreground">
                    {Math.round((salaryIncrease?.confidence || 0) * 100)}%
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-2">Reasoning</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {salaryIncrease?.reasoning || 'Analysis in progress...'}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-3">Negotiation Tips</h4>
              <div className="space-y-2">
                {preparationPlan?.negotiationTips?.slice(0, 3)?.map((tip, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">{tip}</p>
                  </div>
                )) || (
                  <p className="text-sm text-muted-foreground italic">
                    Generating negotiation guidance...
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Strategic Timeline */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="Calendar" size={16} className="text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Strategic Timeline</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Short Term */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-3 flex items-center space-x-2">
              <Icon name="Zap" size={16} className="text-warning" />
              <span>Immediate (1-2 weeks)</span>
            </h4>
            <div className="space-y-2">
              {preparationPlan?.immediateActions?.map((action, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <div className="w-1 h-1 bg-warning rounded-full mt-2 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">{action}</p>
                </div>
              )) || (
                <p className="text-sm text-muted-foreground italic">Loading...</p>
              )}
            </div>
          </div>

          {/* Medium Term */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-3 flex items-center space-x-2">
              <Icon name="Target" size={16} className="text-primary" />
              <span>Short Term (1-3 months)</span>
            </h4>
            <div className="space-y-2">
              {preparationPlan?.shortTermGoals?.map((goal, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <div className="w-1 h-1 bg-primary rounded-full mt-2 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">{goal}</p>
                </div>
              )) || (
                <p className="text-sm text-muted-foreground italic">Loading...</p>
              )}
            </div>
          </div>

          {/* Long Term */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-3 flex items-center space-x-2">
              <Icon name="TrendingUp" size={16} className="text-success" />
              <span>Long Term (3+ months)</span>
            </h4>
            <div className="space-y-2">
              {preparationPlan?.longTermStrategy?.map((strategy, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <div className="w-1 h-1 bg-success rounded-full mt-2 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">{strategy}</p>
                </div>
              )) || (
                <p className="text-sm text-muted-foreground italic">Loading...</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimingRecommendations;